import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { AsyncError, EmptyState, Loading } from '../components/states';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';

interface Summary {
  counts: {
    organizations: number;
    sites: number;
    devices: number;
    fabrics: number;
    activeSessions: number;
    driftedDevices: number;
  };
  devicesByStatus: Record<string, number>;
  openAlertsBySeverity: Record<string, number>;
  deploymentsByStatus: Record<string, number>;
  recentDeployments: Array<{ id: string; status: string; createdAt: string }>;
  recentAlerts: Array<{
    id: string;
    severity: string;
    status: string;
    title: string;
    createdAt: string;
  }>;
}

const SEVERITY_CHIP: Record<string, string> = {
  CRITICAL: 'chip--critical',
  WARNING: 'chip--warning',
  INFO: 'chip--online',
};

export function DashboardPage(): JSX.Element {
  const t = useT();
  const { data, error, loading, reload } = useAsync<Summary>(
    () => api.get<Summary>('/operations/dashboard'),
    [],
  );

  if (loading) return <Loading />;
  if (error) return <AsyncError error={error} onRetry={reload} />;
  if (!data) return <EmptyState hint={t('pages.dashboard.empty')} />;

  const tiles: Array<{ key: string; value: number; to?: string }> = [
    { key: 'nav.organizations', value: data.counts.organizations, to: '/organizations' },
    { key: 'nav.sites', value: data.counts.sites, to: '/sites' },
    { key: 'nav.devices', value: data.counts.devices, to: '/devices' },
    { key: 'nav.fabrics', value: data.counts.fabrics, to: '/fabrics' },
    { key: 'dashboard.activeSessions', value: data.counts.activeSessions },
    { key: 'dashboard.drifted', value: data.counts.driftedDevices, to: '/drift' },
  ];

  return (
    <section>
      <div className="page-head">
        <h1>{t('pages.dashboard.title')}</h1>
      </div>

      <div className="tiles">
        {tiles.map((tile) => {
          const inner = (
            <>
              <span className="tile__value">{tile.value}</span>
              <span className="tile__label">{t(tile.key)}</span>
            </>
          );
          return tile.to ? (
            <Link className="card tile" key={tile.key} to={tile.to}>
              {inner}
            </Link>
          ) : (
            <div className="card tile" key={tile.key}>
              {inner}
            </div>
          );
        })}
      </div>

      <div className="card">
        <h2>{t('dashboard.devicesByStatus')}</h2>
        {renderBreakdown(data.devicesByStatus)}
      </div>

      <div className="card">
        <h2>{t('dashboard.openAlerts')}</h2>
        {Object.keys(data.openAlertsBySeverity).length === 0 ? (
          <EmptyState hint={t('dashboard.noOpenAlerts')} />
        ) : (
          <div className="row-actions">
            {Object.entries(data.openAlertsBySeverity).map(([sev, n]) => (
              <span key={sev} className={`chip ${SEVERITY_CHIP[sev] ?? ''}`}>
                {sev}: {n}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="card">
        <h2>{t('dashboard.recentAlerts')}</h2>
        {data.recentAlerts.length === 0 ? (
          <EmptyState hint={t('dashboard.noRecentAlerts')} />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('dashboard.severity')}</th>
                  <th>{t('dashboard.title')}</th>
                  <th>{t('dashboard.status')}</th>
                  <th>{t('dashboard.when')}</th>
                </tr>
              </thead>
              <tbody>
                {data.recentAlerts.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <span className={`chip ${SEVERITY_CHIP[a.severity] ?? ''}`}>
                        {a.severity}
                      </span>
                    </td>
                    <td>
                      <Link to="/alerts">{a.title}</Link>
                    </td>
                    <td>{a.status}</td>
                    <td>{new Date(a.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h2>{t('dashboard.recentDeployments')}</h2>
        {data.recentDeployments.length === 0 ? (
          <EmptyState hint={t('dashboard.noRecentDeployments')} />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('dashboard.status')}</th>
                  <th>{t('dashboard.when')}</th>
                </tr>
              </thead>
              <tbody>
                {data.recentDeployments.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <Link to={`/deployments/${d.id}`}>{d.status}</Link>
                    </td>
                    <td>{new Date(d.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}

function renderBreakdown(map: Record<string, number>): JSX.Element {
  const entries = Object.entries(map);
  if (entries.length === 0) return <p>—</p>;
  return (
    <div className="row-actions">
      {entries.map(([key, n]) => (
        <span key={key} className="chip">
          {key}: {n}
        </span>
      ))}
    </div>
  );
}
